import { useState, useRef } from "react";
import { format, isBefore, startOfDay, isValid } from "date-fns";
import { Calendar } from "../../ui/calendar";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../lib/utils";
import { CalendarIcon } from "lucide-react";
import useOutsideClick from "../../../hooks/useOutsideClick";

// Helper function to safely parse date values
const parseDate = (value) => {
  if (!value) return null;

  // Handle special case for "immediate launch"
  if (value === "immediate launch") return null;

  // Try to parse as date
  const parsedDate = new Date(value);
  return isValid(parsedDate) ? parsedDate : null;
};

// Helper function to check if a value is a valid date string
const isValidDateValue = (value) => {
  if (!value || value === "immediate launch") return true;
  const parsedDate = new Date(value);
  return isValid(parsedDate);
};

export const DateTimeCell = ({
  value,
  isEditable,
  onSave,
  columnInfo,
  isError,
  isRequired,
  isEmpty,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Safely parse the initial date value
  const initialDate = parseDate(value);
  const [date, setDate] = useState(initialDate);

  // Extract time from valid date or default to "00:00"
  const initialTime = initialDate ? format(initialDate, "HH:mm") : "00:00";
  const [time, setTime] = useState(initialTime);

  const cellRef = useRef(null);
  const popoverRef = useOutsideClick(() => {
    if (isOpen) {
      handleSave();
    }
  }, isOpen);

  const handleSave = () => {
    if (!date) return;

    // Combine date and time
    const [hours, minutes] = time.split(":").map(Number);
    const combinedDateTime = new Date(date);
    combinedDateTime.setHours(hours, minutes);

    // Format the date string to match desired format
    const formattedDate = format(combinedDateTime, "yyyy-MM-dd HH:mm:ss");

    onSave?.(formattedDate);
    setIsOpen(false);
  };

  const handleImmediateLaunch = () => {
    onSave?.(null);
    setIsOpen(false);
  };

  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    // Validate time format (HH:mm)
    if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(newTime)) {
      setTime(newTime);
    }
  };

  const isPastDate = (date) => {
    const now = new Date();
    return isBefore(date, startOfDay(now));
  };

  const isPastTime = (selectedDate, selectedTime) => {
    const now = new Date();
    const [hours, minutes] = selectedTime.split(":").map(Number);
    const selectedDateTime = new Date(selectedDate);
    selectedDateTime.setHours(hours, minutes);
    return isBefore(selectedDateTime, now);
  };

  // Display logic for non-editable cells
  if (!isEditable) {
    if (!value && columnInfo.key === "forecasted_launch_date") {
      return <span>immediate launch</span>;
    }
    if (!value) {
      return <span className="text-gray-400">{columnInfo.display}</span>;
    }

    // Handle invalid date strings
    if (!isValidDateValue(value)) {
      return <span className="text-red-500 italic">Invalid date: {value}</span>;
    }

    // Handle special case
    if (value === "immediate launch") {
      return <span>immediate launch</span>;
    }

    // Format valid date
    return format(new Date(value), "MM/dd/yyyy HH:mm");
  }

  // Display logic for editable cells
  const renderDisplayValue = () => {
    if (!value) {
      return (
        <span className="text-gray-400">
          {columnInfo.key === "forecasted_launch_date"
            ? "immediate launch"
            : columnInfo.display}
        </span>
      );
    }

    if (value === "immediate launch") {
      return "immediate launch";
    }

    // Handle invalid date strings in editable mode
    if (!isValidDateValue(value)) {
      return <span className="text-red-500 italic">Invalid date: {value}</span>;
    }

    // Format valid date
    return format(new Date(value), "MM/dd/yyyy HH:mm");
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          ref={cellRef}
          className={cn(
            "cursor-pointer hover:border hover:border-blue-300 hover:bg-blue-50 rounded p-px",
            isError && "border border-red-500 bg-red-50",
            // Add visual indicator for invalid dates
            value &&
              !isValidDateValue(value) &&
              "border border-orange-500 bg-orange-50"
          )}
        >
          {isRequired && isEmpty && (
            <span
              className="text-red-500 mr-1 align-middle"
              title="This is a required field."
            >
              *
            </span>
          )}
          <div className="flex items-center gap-2">
            {renderDisplayValue()}
            <CalendarIcon className="h-4 w-4 text-gray-500" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent ref={popoverRef} className="w-auto p-0" align="start">
        <div className="p-3 space-y-4">
          {/* Show warning for invalid date values */}
          {value && !isValidDateValue(value) && (
            <div className="text-sm text-orange-600 bg-orange-50 p-2 rounded">
              Current value &quot;{value}&quot; is not a valid date. Please
              select a new date.
            </div>
          )}

          {columnInfo.key === "forecasted_launch_date" && (
            <Button
              variant="outline"
              className="w-full"
              onClick={handleImmediateLaunch}
            >
              immediate launch
            </Button>
          )}
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            initialFocus
            disabled={(date) => isPastDate(date)}
          />
          <div className="flex items-center gap-2">
            <Input
              type="time"
              value={time}
              onChange={handleTimeChange}
              className="w-[120px]"
              min={
                date && isPastDate(date)
                  ? format(new Date(), "HH:mm")
                  : undefined
              }
            />
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!date || (date && isPastTime(date, time))}
            >
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
